/**
 * Shared date availability utilities
 * Used across TourPage, GroupTourPage, OtherTour
 * Preserves all business logic exactly
 */

import { handleAvailableDatesFormat } from '../helpers';

/**
 * The admin saves ranges as instants (local midnight in Armenia) and the server decides
 * bookability on the Asia/Yerevan calendar day of each bound
 * (ayrarat-api BookingController::AVAILABILITY_TIMEZONE). The client reads the same day,
 * so a visitor in any time zone sees the days the server accepts.
 */
export const AVAILABILITY_TIME_ZONE = 'Asia/Yerevan';

const availabilityDayFormat = new Intl.DateTimeFormat('en-CA', {
    timeZone: AVAILABILITY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

// Calendar day (yyyy-mm-dd) of a stored bound in Asia/Yerevan, or null when unreadable.
// A plain yyyy-mm-dd is that day as-is (new Date() reads it as UTC midnight, which is
// the same day in Yerevan).
export const toAvailabilityDay = (value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const parts = {};
    availabilityDayFormat.formatToParts(d).forEach(p => { parts[p.type] = p.value; });
    return `${parts.year}-${parts.month}-${parts.day}`;
};

// The picker hands over local midnight of the day the visitor clicked: that day, as yyyy-mm-dd.
const pad = (n) => String(n).padStart(2, '0');
const toPickerDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toDayRange = (range) => {
    if (!range || !range.start || !range.end) return null;
    const start = toAvailabilityDay(range.start);
    const end = toAvailabilityDay(range.end);
    return start && end ? { start, end } : null;
};

const inDayRange = (day, range) => range !== null && day >= range.start && day <= range.end;

export const checkDateAvailability = (date, data, availableDateRanges) => {
    if (!data) return false;

    const day = toPickerDay(date);

    const unAvailable = handleAvailableDatesFormat(data?.un_available_dates || '{}').map(toDayRange);
    if (unAvailable.some(range => inDayRange(day, range))) return false;

    // Use the pre-parsed array appended by Eloquent ($appends → available_weekdays_array).
    // data.available_weekdays is the raw JSON string and Array.isArray() on it is always false.
    const weekdays =
        Array.isArray(data.available_weekdays_array) && data.available_weekdays_array.length > 0
            ? data.available_weekdays_array
            : null;

    for (const range of availableDateRanges) {
        if (!inDayRange(day, toDayRange(range))) continue;

        if (weekdays !== null) {
            if (weekdays.indexOf(date.getDay()) !== -1) return true;
        } else {
            return true;
        }
    }

    return false;
};

export const processAvailableDates = (availableDates) => {
    if (!availableDates) return [];
    
    const ranges = handleAvailableDatesFormat(availableDates);
    if (ranges && ranges.length > 0) {
        return ranges;
    }
    
    return [];
};
